import React, { useEffect, useState } from "react";
import {
  IconButton,
  Typography,
  Button,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Card,
  Tooltip,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Select,
  MenuItem,
  FormControl,
} from "@mhome/ui";
import { CircularProgress } from "@mui/material";
import {
  userInfoState,
  platformState,
  activeScopeState,
  scopesState,
  developerEmailState,
  wsReadyState,
  scopePreferenceState,
} from "../global-state";
import { useAppInterface } from "../app/app-interface-provider";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import EditOffIcon from "@mui/icons-material/EditOff";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import { AddCircle as AddCircleIcon } from "@mui/icons-material";
import { RemoveCircle as RemoveCircleIcon } from "@mui/icons-material";
import { useComponentBackgroundColor } from "../common/adaptive-theme-provider";
import { useWebSocket } from "../ws/websocket";
import {
  saveScope,
  deleteScope,
  deleteScopeMembership,
  inviteMember,
  updateMember,
  getScopeInfo,
  listScopeInfo,
  listScopePreferences,
  exitScope,
  switchScope,
  saveScopeExecutionEnv,
  listInvitation,
  cleanScopeData,
  deleteLocation,
  listLocations,
  saveLocation,
} from "../service/scope-service";
import LocationDialog from "./location-dialog";
import { useMediaQuery } from "@mui/material";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { useHistory } from "react-router-dom";
import InfoIcon from "@mui/icons-material/Info";
import LocationOnIcon from "@mui/icons-material/LocationOn";

import { useToast } from "../common/toast";
import { spacingToPx, convertSxToStyle } from "../lib/utils";

const Scope = () => {
  const [selectedScope, setSelectedScope] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isScopeEditing, setIsScopeEditing] = useState(false);
  const [newScopeName, setNewScopeName] = useState("");
  const [newMembers, setNewMembers] = useState([]);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [selectedEnv, setSelectedEnv] = useState(null);
  const [editedName, setEditedName] = useState("");
  const [scopes, setScopes] = useRecoilState(scopesState);
  const setScopePreference = useSetRecoilState(scopePreferenceState);
  const [newSpaceLocation, setNewSpaceLocation] = useState(null);
  const [newSpaceLocationDialogOpen, setNewSpaceLocationDialogOpen] =
    useState(false);
  const userInfo = useRecoilValue(userInfoState);
  const [activeScope] = useRecoilState(activeScopeState);
  const [developerEmail] = useRecoilState(developerEmailState);
  const appInterface = useAppInterface();

  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [isOwner, setIsOwner] = useState(false);
  const { successToast, warningToast, errorToast } = useToast();
  const [error, setError] = useState("");
  const { sendAndWaitWs, changeActiveScope } = useWebSocket();
  const getComponentBgColor = useComponentBackgroundColor();
  const isSmallScreen = useMediaQuery("@media (max-width:599px)");
  const [currentOperation, setCurrentOperation] = useState(null); // type: 'EDIT_MEMBERS', 'EDIT_SCOPE', 'ADD_SCOPE', null
  const [isRunning, setIsRunning] = useState(null); // type: 'ADD_MEMBER', 'DELE_SCOPE', 'ADD_SCOPE', null

  const [invitations, setInvitations] = useState([]);
  const [loadingInvitationIds, setLoadingInvitationIds] = useState({});
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmDeleteText, setConfirmDeleteText] = useState("");
  const [confirmExitOpen, setConfirmExitOpen] = useState(false);
  const [platform] = useRecoilState(platformState);
  const [emailErrors, setEmailErrors] = useState(
    Array(newMembers.length).fill("")
  );

  // Location state
  const [locations, setLocations] = useState([]);
  const [homeLocation, setHomeLocation] = useState(null);
  const [otherLocations, setOtherLocations] = useState([]);
  const [locationNames, setLocationNames] = useState([]);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [editLocation, setEditLocation] = useState(null);
  const [isHomeLocationDialog, setIsHomeLocationDialog] = useState(false);

  // Fix Data states
  const [cleanDataDialogOpen, setCleanDataDialogOpen] = useState(false);
  const [cleanDataRunning, setCleanDataRunning] = useState(false);
  const wsReady = useRecoilValue(wsReadyState);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchAll();
      setLoading(false);
    };
    if (wsReady) {
      loadData();
    }
  }, [wsReady, activeScope]);

  useEffect(() => {
    setEditedName(selectedScope.name);
  }, [scopes, selectedScope]);

  const fetchAll = async () => {
    try {
      await Promise.all([fetchScopes(), fetchInvitations()]);
    } catch (error) {
      console.error("Error fetching scopes/invitations:", error);
    }

    if (activeScope) {
      try {
        await fetchLocations(activeScope);
      } catch (error) {
        console.error("Error fetching locations:", error);
      }
    }
  };

  const fetchScopes = async () => {
    const scopeResp = await listScopeInfo(sendAndWaitWs);
    if (scopeResp && !scopeResp.error) {
      if (scopeResp.length === 0) {
        changeActiveScope(null);
        const scopeInfo = {
          scopes: [],
          activeScope: null,
        };
        if (appInterface.isAvailable()) {
          await appInterface.saveScopes(scopeInfo);
        }
        history.push("/init-scope");
        return;
      }
      const scopePreferencesResp = await listScopePreferences(sendAndWaitWs);
      setScopes(scopeResp);
      setScopePreference(scopePreferencesResp);
      let activeScopeData = scopeResp.find((s) => s.id === activeScope);
      if (!activeScopeData) {
        activeScopeData = scopeResp.find((s) => s.defaultActive);
      }
      changeActiveScope(activeScopeData?.id);
      setSelectedScope(activeScopeData);
      await handleScopeInfo(activeScopeData.id);
      if (appInterface.isAvailable()) {
        const scopeInfo = {
          scopes: scopeResp,
          scopePreferences: scopePreferencesResp,
          activeScope: activeScopeData.id,
        };
        await appInterface.saveScopes(scopeInfo);
      }
    } else {
      errorToast("Error!");
    }
    return scopeResp;
  };

  const handleScopeInfo = async (scopeId) => {
    const resp = await getScopeInfo(sendAndWaitWs, scopeId);
    if (resp && resp.members) {
      setFamilyMembers(resp.members);
      const o = resp.members.some(
        (member) => member.email === userInfo.name && member.role === "OWNER"
      );
      if (o) {
        setIsOwner(true);
      } else {
        setIsOwner(false);
      }
      let env = resp.executionEnv;
      if (env === "LOCAL-" + developerEmail) {
        env = "LOCAL";
      }
      setSelectedEnv(env);
    }
  };

  const fetchInvitations = async () => {
    const resp = await listInvitation(sendAndWaitWs);
    if (resp && !resp.error) {
      if (Array.isArray(resp) && resp.length > 0) {
        setInvitations(resp);
      } else {
        setInvitations([]);
      }
    } else {
      errorToast("Failed to load invitations");
    }
  };

  const handleRejectInvitation = async (invitationScopeId) => {
    setIsRunning("REJECT");
    setLoadingInvitationIds((prev) => ({ ...prev, [invitationScopeId]: true }));
    let scopeId = null;
    invitations
      .filter((inv) => inv.scopeId === invitationScopeId)
      .map((inv) => {
        scopeId = inv.scopeId;
      });

    if (!scopeId) {
      errorToast("The space not found.");
      return;
    }
    const resp = await updateMember(sendAndWaitWs, scopeId, false);
    if (resp && !resp.error) {
      successToast("Successfully reject the invitation");
      setInvitations(
        invitations.filter((inv) => inv.scopeId !== invitationScopeId)
      );
      await fetchInvitations();
    } else {
      errorToast("Failed to reject invitation!");
    }
    setIsRunning(null);
    setLoadingInvitationIds((prev) => ({
      ...prev,
      [invitationScopeId]: false,
    }));
  };

  const handleAcceptInvitation = async (invitationScopeId) => {
    console.debug(invitationScopeId);
    setIsRunning("ACCEPT");
    setLoadingInvitationIds((prev) => ({ ...prev, [invitationScopeId]: true }));
    let scopeId = null;
    invitations
      .filter((inv) => inv.scopeId === invitationScopeId)
      .map((inv) => {
        scopeId = inv.scopeId;
      });
    if (!scopeId) {
      errorToast("The space not found.");
      return;
    }
    const resp = await updateMember(sendAndWaitWs, scopeId, true, true);
    if (resp && !resp.error) {
      successToast("Successfully joined the space");
      setInvitations(
        invitations.filter((inv) => inv.scopeId !== invitationScopeId)
      );
      await fetchInvitations();
      await fetchScopes();
    } else {
      errorToast("Failed to accept invitation!");
    }
    setIsRunning(null);
    setLoadingInvitationIds((prev) => ({
      ...prev,
      [invitationScopeId]: false,
    }));
  };

  const startOperation = (operation) => {
    if (currentOperation) {
      warningToast("Please complete current operation first");
      return false;
    }
    setCurrentOperation(operation);
    return true;
  };

  const endOperation = () => {
    setCurrentOperation(null);
  };

  const handleEditScope = () => {
    if (startOperation("EDIT_SCOPE")) {
      setIsScopeEditing(true);
      setEditedName(selectedScope.name);
    }
  };

  const handleExitScope = () => {
    setConfirmExitOpen(true);
  };

  const handleConfirmExitScope = async () => {
    const resp = await exitScope(sendAndWaitWs);
    if (resp && !resp.error) {
      successToast("Exited");
    } else {
      errorToast("Error!");
    }
    setConfirmExitOpen(false);
    fetchScopes();
  };

  const handleSwitchScope = async (oldId, scopeId) => {
    if (oldId === scopeId) {
      return;
    }
    setLoading(true);
    const resp = await switchScope(sendAndWaitWs, scopeId);
    if (resp && !resp.error) {
      // Set the active scope in global state
      changeActiveScope(scopeId);
      if (appInterface.isAvailable()) {
        await appInterface.saveScopes({ scopes, activeScope: scopeId });
      }
      successToast("Switched Space");
    } else {
      errorToast("Error!");
    }
    setLoading(false);
  };

  const handleSaveEditScope = async () => {
    const resp = await saveScope(sendAndWaitWs, selectedScope.id, editedName);
    if (resp && !resp.error) {
      fetchScopes();
      setIsScopeEditing(false);
      endOperation();
    } else {
      errorToast("Error!");
    }
  };

  const handleCancelEditScope = () => {
    setIsScopeEditing(false);
    setEditedName(selectedScope.name);
    endOperation();
  };

  const handleDeleteScope = async () => {
    setIsRunning("DELE_SCOPE");
    const resp = await deleteScope(sendAndWaitWs);
    if (resp && !resp.error) {
      successToast("Deleted");
    } else {
      errorToast("Error!");
    }
    fetchScopes();
    setIsRunning(null);
    endOperation();
    setConfirmDeleteOpen(false);
  };

  const isDuplicateName = (name) => {
    return scopes.some(
      (scope) => scope.name.toLowerCase() === name.toLowerCase().trim()
    );
  };

  const checkDuplicateName = (name) => {
    const trimmedName = name.toLowerCase().trim();
    if (!trimmedName) {
      setError("name cannot be empty");
      return true;
    } else if (isDuplicateName(trimmedName)) {
      setError("name already exists");
      return true;
    } else {
      setError("");
      return false;
    }
  };

  const handleAddScope = async () => {
    setIsRunning("ADD_SCOPE");
    setError("");
    const trimmedName = newScopeName.toLowerCase().trim();
    if (!trimmedName) {
      setError("name cannot be empty");
      setIsRunning(false);
      return;
    } else if (checkDuplicateName(trimmedName)) {
      setIsRunning(false);
      return;
    } else if (!newSpaceLocation) {
      setError("home location is required");
      setIsRunning(false);
      return;
    }
    const resp = await saveScope(sendAndWaitWs, "new", newScopeName);
    if (resp && !resp.error) {
      const scopeId = resp.scopeId;
      const locationData = {
        name: "Home",
        address: newSpaceLocation.address,
        latitude: newSpaceLocation.latitude,
        longitude: newSpaceLocation.longitude,
        timezone: newSpaceLocation.timezone,
        locationId: "home",
      };

      await saveLocation(sendAndWaitWs, scopeId, locationData);

      // Switch to the new scope and set active scope
      await handleSwitchScope(selectedScope.id, scopeId);

      setOpenDialog(false);
      setNewScopeName("");
      setNewSpaceLocation(null);
      setError("");
      endOperation();
      successToast("Space and home location created successfully!");
    } else {
      errorToast("Failed to create space!");
    }
    setIsRunning(false);
  };

  const handleScopeChange = (event) => {
    const scope = scopes.find((s) => s.name === event.target.value);
    const oldId = selectedScope.id;
    setSelectedScope(scope);
    handleSwitchScope(oldId, scope.id);
    localStorage.removeItem("notifications");
  };

  const handleNewScope = () => {
    if (startOperation("ADD_SCOPE")) {
      setOpenDialog(true);
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setNewScopeName("");
    setNewSpaceLocation(null);
    setError("");
    endOperation();
  };

  const handleNewSpaceLocationSave = (locationData) => {
    setNewSpaceLocation(locationData);
    setNewSpaceLocationDialogOpen(false);
    setError(""); // Clear any previous error when location is selected
  };

  const handleEditToggle = () => {
    if (isEditing) {
      setIsEditing(false);
      endOperation();
    } else {
      if (startOperation("EDIT_MEMBERS")) {
        setIsEditing(true);
      }
    }
  };

  const handleAddMemberOpen = () => {
    setNewMembers([""]);
    setOpenAddDialog(true);
  };

  const handleAddMemberClose = () => {
    setNewMembers([]);
    setOpenAddDialog(false);
  };

  const handleMemberChange = (index, value) => {
    const updatedMembers = [...newMembers];
    updatedMembers[index] = value;
    setNewMembers(updatedMembers);

    const errors = [...emailErrors];
    errors[index] = validateEmail(value, updatedMembers);
    setEmailErrors(errors);
  };

  const handleRemoveMember = (index) => {
    const updatedMembers = newMembers.filter((_, i) => i !== index);
    setNewMembers(updatedMembers);

    setEmailErrors(
      updatedMembers.map((member) => validateEmail(member, updatedMembers))
    );
  };

  const validateEmail = (email, members) => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) return "Email cannot be empty";
    if (!emailPattern.test(email)) return "Invalid email format";
    if (members.filter((member) => member === email).length > 1)
      return "Duplicate email";
    if (familyMembers.some((member) => member.email === email))
      return "User already invited";
    return "";
  };

  const handleAddMembers = async () => {
    setIsRunning("ADD_MEMBER");
    const errors = newMembers.map((member) =>
      validateEmail(member, newMembers)
    );
    if (errors.some((error) => error)) {
      setEmailErrors(errors);
      setIsRunning(null);
      return;
    }
    setNewMembers([...newMembers]);
    const resp = await inviteMember(sendAndWaitWs, newMembers);
    if (resp.failed && resp.failed.length > 0) {
      errorToast(`Failed to invite: ${resp.failed.join(", ")}`);
    } else if (resp.invited) {
      successToast(`Invited ${resp.invited.length} users`);
    } else {
      errorToast("Error!");
    }
    setOpenAddDialog(false);
    setIsRunning(null);
    handleScopeInfo(selectedScope.id);
  };

  const handleDeleteMember = async (email) => {
    const resp = await deleteScopeMembership(sendAndWaitWs, email);
    if (resp && !resp.error) {
      successToast("Deleted");
    } else {
      errorToast("Error!");
    }
    handleScopeInfo(selectedScope.id);
  };

  const handleExecutionEnvChange = async (newEnv) => {
    if (!developerEmail) {
      errorToast("Error!");
    }
    let env = newEnv;
    if (env === "LOCAL") {
      env = env + "-" + developerEmail;
    }
    const resp = await saveScopeExecutionEnv(sendAndWaitWs, env);
    if (resp && !resp.error) {
      successToast("Updated!");
    } else {
      errorToast("Error!");
    }
    handleScopeInfo(selectedScope.id);
  };

  const fetchLocations = async (scopeId = null) => {
    const currentScopeId = scopeId || activeScope;
    const resp = await listLocations(sendAndWaitWs, currentScopeId);
    if (resp && !resp.error) {
      setLocations(resp.locations);
      setLocationNames(resp.locations.map((location) => location.name));

      // Separate home and other locations
      const home = resp.locations.find((loc) => loc.locationId === "home");
      const others = resp.locations.filter((loc) => loc.locationId !== "home");

      setHomeLocation(home);
      setOtherLocations(others);
    } else {
      errorToast("Failed to load locations");
    }
  };

  const handleAddLocation = () => {
    setEditLocation(null);
    setIsHomeLocationDialog(false);
    setLocationDialogOpen(true);
  };

  const handleAddHomeLocation = () => {
    setEditLocation(null);
    setIsHomeLocationDialog(true);
    setLocationDialogOpen(true);
  };

  const handleEditLocation = (location) => {
    setEditLocation(location);
    setIsHomeLocationDialog(location.locationId === "home");
    setLocationDialogOpen(true);
  };

  const handleSaveLocation = async (locationData) => {
    const resp = await saveLocation(sendAndWaitWs, activeScope, locationData);
    if (resp && !resp.error) {
      successToast("Location saved");
      await fetchLocations(activeScope);
    } else {
      errorToast("Failed to save location");
    }
  };

  const handleDeleteLocation = async (locationData) => {
    const resp = await deleteLocation(sendAndWaitWs, activeScope, locationData);
    if (resp && !resp.error) {
      successToast("Location deleted");
      await fetchLocations(activeScope);
    } else {
      errorToast("Failed to delete location");
    }
  };

  const handleCleanData = async (dryRun = false) => {
    setCleanDataRunning(true);
    const response = await cleanScopeData(sendAndWaitWs, dryRun);
    if (response && !response.error) {
      successToast(
        response.message ||
          `${dryRun ? "Dry run completed" : "Data cleaned successfully"}`
      );
    } else {
      errorToast("Failed to clean data");
    }
    setCleanDataRunning(false);
    setCleanDataDialogOpen(false);
  };

  return (
    <>
      {loading ? (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            padding: spacingToPx(3),
          }}
        >
          <CircularProgress />
        </div>
      ) : (
        <div
          style={{
            width: "100%",
            height: "100%",
            padding: spacingToPx(2),
            paddingBottom: "100px",
          }}
        >
          {/* Content Section */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: spacingToPx(2),
              width: "100%",
            }}
          >
            <Card
              style={{
                width: "100%",
                backgroundColor: getComponentBgColor("hsl(var(--card))"),
                fontFamily: "Roboto, sans-serif",
                boxShadow: "none",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  minHeight: "40px",
                  paddingLeft: spacingToPx(isSmallScreen ? 1.5 : 2),
                  paddingRight: spacingToPx(isSmallScreen ? 1.5 : 2),
                  paddingTop: spacingToPx(1),
                  paddingBottom: spacingToPx(1),
                }}
              >
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                  Space Management
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNewScope}
                  startIcon={<AddIcon />}
                  style={{
                    textTransform: "none",
                    fontSize: isSmallScreen ? "0.75rem" : "0.875rem",
                    fontWeight: 500,
                    boxShadow: "none",
                    whiteSpace: "nowrap",
                    height: "32px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = "none";
                    // Darken the primary color on hover using filter
                    e.currentTarget.style.filter = "brightness(0.85)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.filter = "none";
                  }}
                  size="small"
                >
                  New Space
                </Button>
              </div>
              <Divider
                style={{
                  marginBottom: spacingToPx(2),
                  marginLeft: spacingToPx(2),
                  marginRight: spacingToPx(2),
                }}
              />
              <div
                style={{
                  paddingLeft: spacingToPx(isSmallScreen ? 1.5 : 2),
                  paddingRight: spacingToPx(isSmallScreen ? 1.5 : 2),
                  paddingBottom: spacingToPx(isSmallScreen ? 1.5 : 2),
                }}
              >
                <div style={{ marginBottom: spacingToPx(2) }}>
                  <Typography
                    variant="body2"
                    style={{ fontWeight: 500, marginBottom: spacingToPx(1) }}
                  >
                    Current Space
                  </Typography>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: spacingToPx(1),
                      marginBottom: spacingToPx(1),
                    }}
                  >
                    {isScopeEditing ? (
                      <TextField
                        value={editedName}
                        onChange={(e) => setEditedName(e.target.value)}
                        size="small"
                        variant="outlined"
                        fullWidth
                      />
                    ) : (
                      selectedScope && (
                        <FormControl variant="outlined" size="small" fullWidth>
                          <Select
                            value={selectedScope?.name || ""}
                            onChange={handleScopeChange}
                            variant="outlined"
                            size="small"
                            style={convertSxToStyle({
                              flex: 1,
                              color: "inherit",
                              fontSize: isSmallScreen ? "0.875rem" : "1rem",
                              backgroundColor:
                                getComponentBgColor("hsl(var(--card))"),
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "hsl(var(--border))",
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "hsl(var(--primary))",
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                {
                                  borderColor: "hsl(var(--primary))",
                                },
                              "& .MuiSelect-select": {
                                padding: "8px 12px",
                                display: "flex",
                                alignItems: "center",
                              },
                              "& .MuiSelect-icon": {
                                color: "hsl(var(--foreground))",
                              },
                            })}
                            menuStyle={
                              {
                                // Customize dropdown menu styles here
                                // border: "1px solid hsl(var(--border))",
                                // borderRadius: "8px",
                              }
                            }
                          >
                            {scopes.map((scope) => (
                              <MenuItem
                                key={scope.id}
                                value={scope.name}
                                className="overflow-hidden whitespace-nowrap max-w-full"
                                style={{
                                  fontSize: isSmallScreen ? "0.875rem" : "1rem",
                                  padding: "8px 12px",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "hsl(var(--accent))";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "transparent";
                                }}
                              >
                                <Typography
                                  noWrap
                                  className="overflow-hidden whitespace-nowrap max-w-full"
                                  style={{
                                    fontWeight:
                                      scope.id === selectedScope.id ? 500 : 400,
                                  }}
                                >
                                  {scope.name}
                                </Typography>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )
                    )}
                  </div>
                </div>

                <div>
                  <Typography
                    variant="body2"
                    style={{ fontWeight: 500, marginBottom: spacingToPx(1) }}
                  >
                    Space Actions
                  </Typography>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "stretch",
                      gap: spacingToPx(1),
                      flexDirection: isSmallScreen ? "column" : "row",
                      flexWrap: "nowrap",
                    }}
                  >
                    {isScopeEditing ? (
                      <>
                        <Button
                          variant="contained"
                          onClick={handleSaveEditScope}
                          className="flex-1 whitespace-nowrap"
                          style={{
                            textTransform: "none",
                            minWidth: "80px",
                            height: "32px",
                          }}
                          size="small"
                        >
                          Save
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={handleCancelEditScope}
                          className="flex-1 whitespace-nowrap"
                          style={{
                            textTransform: "none",
                            minWidth: "80px",
                            height: "32px",
                          }}
                          size="small"
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        {isOwner ? (
                          <>
                            <Button
                              variant="outlined"
                              onClick={handleEditScope}
                              startIcon={<EditIcon />}
                              className="flex-1 whitespace-nowrap"
                              style={{
                                textTransform: "none",
                                opacity: currentOperation ? 0.5 : 1,
                                pointerEvents: currentOperation
                                  ? "none"
                                  : "auto",
                                minWidth: "120px",
                                height: "32px",
                              }}
                              disabled={!!currentOperation}
                              size="small"
                            >
                              Edit Name
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              onClick={() => {
                                setConfirmDeleteOpen(true);
                              }}
                              startIcon={<DeleteIcon />}
                              className="flex-1 whitespace-nowrap"
                              style={{
                                textTransform: "none",
                                opacity: currentOperation ? 0.5 : 1,
                                pointerEvents: currentOperation
                                  ? "none"
                                  : "auto",
                                minWidth: "120px",
                                height: "32px",
                              }}
                              disabled={
                                !!currentOperation || isRunning === "DELE_SCOPE"
                              }
                              size="small"
                            >
                              Delete Space
                            </Button>
                          </>
                        ) : (
                          <Button
                            variant="outlined"
                            color="warning"
                            onClick={handleExitScope}
                            className="flex-1 whitespace-nowrap"
                            style={{
                              textTransform: "none",
                              opacity: currentOperation ? 0.5 : 1,
                              pointerEvents: currentOperation ? "none" : "auto",
                              minWidth: "120px",
                              height: "32px",
                            }}
                            disabled={!!currentOperation}
                            size="small"
                          >
                            Exit Space
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </Card>

            <Card
              style={{
                width: "100%",
                backgroundColor: getComponentBgColor("hsl(var(--card))"),
                fontFamily: "Roboto, sans-serif",
                boxShadow: "none",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  minHeight: "40px",
                  paddingLeft: spacingToPx(isSmallScreen ? 1.5 : 2),
                  paddingRight: spacingToPx(isSmallScreen ? 1.5 : 2),
                }}
              >
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                  Locations
                </Typography>
              </div>
              <Divider
                style={{
                  marginBottom: spacingToPx(2),
                  marginLeft: spacingToPx(2),
                  marginRight: spacingToPx(2),
                }}
              />

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: spacingToPx(1),
                  paddingLeft: spacingToPx(isSmallScreen ? 1.5 : 2),
                  paddingRight: spacingToPx(isSmallScreen ? 1.5 : 2),
                  paddingBottom: spacingToPx(isSmallScreen ? 1.5 : 2),
                }}
              >
                {/* Home Location Section */}
                <div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: spacingToPx(1),
                    }}
                  >
                    <Typography
                      variant="body2"
                      style={{
                        fontWeight: 500,
                        color: "hsl(var(--foreground))",
                      }}
                    >
                      Home Location
                    </Typography>
                    {!homeLocation && (
                      <Tooltip title="Add home location">
                        <IconButton
                          onClick={handleAddHomeLocation}
                          color="primary"
                          size="small"
                        >
                          <AddIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>

                  {homeLocation ? (
                    <div
                      onClick={() => handleEditLocation(homeLocation)}
                      style={{
                        marginBottom: spacingToPx(0.25),
                        backgroundColor:
                          getComponentBgColor("hsl(var(--card))"),
                        borderRadius: "16px",
                        padding: spacingToPx(1),
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor =
                          "hsl(var(--accent))";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor =
                          getComponentBgColor("hsl(var(--card))");
                      }}
                    >
                      <Typography variant="body2" style={{ fontWeight: 500 }}>
                        {homeLocation.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        style={{ color: "hsl(var(--muted-foreground))" }}
                      >
                        {homeLocation.address}
                      </Typography>
                    </div>
                  ) : (
                    <Typography
                      variant="caption"
                      style={{
                        color: "hsl(var(--muted-foreground))",
                        paddingLeft: spacingToPx(1),
                      }}
                    >
                      No home location set
                    </Typography>
                  )}
                </div>

                {/* Other Locations Section */}
                <div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: spacingToPx(0.5),
                    }}
                  >
                    <Typography
                      variant="body2"
                      style={{
                        fontWeight: 500,
                        color: "hsl(var(--foreground))",
                      }}
                    >
                      Other Locations
                    </Typography>
                    <Tooltip title="Add location">
                      <IconButton
                        onClick={handleAddLocation}
                        color="primary"
                        size="small"
                      >
                        <AddIcon />
                      </IconButton>
                    </Tooltip>
                  </div>

                  {otherLocations.length > 0 ? (
                    <List style={{ paddingTop: 0, paddingBottom: 0 }}>
                      {otherLocations.map((location) => (
                        <ListItem
                          key={location.id}
                          onClick={() => handleEditLocation(location)}
                          style={{
                            marginBottom: spacingToPx(1),
                            backgroundColor:
                              getComponentBgColor("hsl(var(--card))"),
                            borderRadius: "16px",
                            paddingTop: spacingToPx(0.5),
                            paddingBottom: spacingToPx(0.5),
                            paddingLeft: spacingToPx(1),
                            paddingRight: spacingToPx(1),
                            cursor: "pointer",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor =
                              "hsl(var(--accent))";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                              getComponentBgColor("hsl(var(--card))");
                          }}
                        >
                          <ListItemText
                            primary={location.name}
                            secondary={location.address}
                            primaryTypographyProps={{
                              variant: "body2",
                              style: { fontWeight: 500 },
                            }}
                            secondaryTypographyProps={{
                              variant: "caption",
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography
                      variant="caption"
                      style={{
                        color: "hsl(var(--muted-foreground))",
                        paddingLeft: spacingToPx(1),
                      }}
                    >
                      No other locations
                    </Typography>
                  )}
                </div>
              </div>
            </Card>

            <Card
              style={{
                width: "100%",
                backgroundColor: getComponentBgColor("hsl(var(--card))"),
                fontFamily: "Roboto, sans-serif",
                boxShadow: "none",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  minHeight: "40px",
                  paddingLeft: spacingToPx(isSmallScreen ? 1.5 : 2),
                  paddingRight: spacingToPx(isSmallScreen ? 1.5 : 2),
                }}
              >
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                  Members
                </Typography>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: spacingToPx(1),
                  }}
                >
                  {isOwner && (
                    <IconButton
                      color="primary"
                      style={{
                        opacity:
                          currentOperation &&
                          currentOperation !== "EDIT_MEMBERS"
                            ? 0.5
                            : 1,
                        pointerEvents:
                          currentOperation &&
                          currentOperation !== "EDIT_MEMBERS"
                            ? "none"
                            : "auto",
                        padding: spacingToPx(isSmallScreen ? 0.5 : 1),
                      }}
                      onClick={handleEditToggle}
                      disabled={
                        currentOperation && currentOperation !== "EDIT_MEMBERS"
                      }
                    >
                      {isEditing ? <EditOffIcon /> : <EditIcon />}
                    </IconButton>
                  )}
                </div>
              </div>
              <Divider
                style={{
                  marginBottom: spacingToPx(2),
                  marginLeft: spacingToPx(2),
                  marginRight: spacingToPx(2),
                }}
              />

              <div
                style={{
                  paddingLeft: spacingToPx(isSmallScreen ? 1.5 : 2),
                  paddingRight: spacingToPx(isSmallScreen ? 1.5 : 2),
                  paddingBottom: spacingToPx(isSmallScreen ? 1.5 : 2),
                }}
              >
                <List style={{ paddingTop: 0, paddingBottom: 0 }}>
                  {familyMembers.map((member, index) => (
                    <ListItem
                      key={member.email}
                      alignItems="center"
                      style={{
                        marginTop: index === 0 ? 0 : spacingToPx(1),
                        marginBottom:
                          index === familyMembers.length - 1
                            ? 0
                            : spacingToPx(1),
                        backgroundColor:
                          getComponentBgColor("hsl(var(--card))"),
                        borderRadius: "16px",
                        paddingTop: spacingToPx(1),
                        paddingBottom: spacingToPx(1),
                        paddingLeft: spacingToPx(0.75),
                        paddingRight: spacingToPx(0.75),
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          style={{
                            backgroundColor:
                              "hsl(var(--muted-foreground) / 0.3)",
                            color: "hsl(var(--foreground))",
                            fontSize: "0.6rem",
                            width: 28,
                            height: 28,
                          }}
                        >
                          {member.email[0].toUpperCase()}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: isSmallScreen ? "150px" : "250px",
                              fontWeight: 500,
                            }}
                          >
                            {member.email}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            variant="caption"
                            component="span"
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: isSmallScreen ? "150px" : "250px",
                            }}
                          >
                            {member.role.toLowerCase()}
                            {!member.pending ? null : (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                component="span"
                              >
                                &nbsp;(Pending...)
                              </Typography>
                            )}
                          </Typography>
                        }
                      />

                      {isOwner && isEditing && (
                        <>
                          {member.role !== "OWNER" && (
                            <IconButton
                              edge="end"
                              aria-label="delete"
                              onClick={() => {
                                handleDeleteMember(member.email);
                              }}
                              color="error"
                              style={{
                                padding: spacingToPx(isSmallScreen ? 0.5 : 1),
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </>
                      )}
                    </ListItem>
                  ))}
                </List>
                {isEditing && isOwner && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      marginTop: spacingToPx(1),
                      gap: spacingToPx(0.5),
                    }}
                  >
                    <IconButton
                      onClick={handleAddMemberOpen}
                      color="primary"
                      size="small"
                      style={{
                        borderRadius: "50%",
                        padding: spacingToPx(0.5),
                      }}
                    >
                      <AddCircleIcon />
                    </IconButton>
                    <Typography
                      onClick={handleAddMemberOpen}
                      variant="body2"
                      style={{
                        color: "hsl(var(--primary))",
                        cursor: "pointer",
                      }}
                    >
                      invite members
                    </Typography>
                  </div>
                )}
              </div>
            </Card>

            <Card
              style={{
                width: "100%",
                backgroundColor: getComponentBgColor("hsl(var(--card))"),
                fontFamily: "Roboto, sans-serif",
                boxShadow: "none",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  minHeight: "40px",
                  paddingLeft: spacingToPx(isSmallScreen ? 1.5 : 2),
                  paddingRight: spacingToPx(isSmallScreen ? 1.5 : 2),
                }}
              >
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                  Invitations
                </Typography>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: spacingToPx(1),
                  }}
                >
                  <IconButton
                    onClick={fetchInvitations}
                    color="primary"
                    style={{ padding: spacingToPx(isSmallScreen ? 0.5 : 1) }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </div>
              </div>
              <Divider
                style={{
                  marginBottom: spacingToPx(2),
                  marginLeft: spacingToPx(2),
                  marginRight: spacingToPx(2),
                }}
              />

              {invitations.length > 0 &&
                invitations.map((invitation, index) => (
                  <div
                    key={`${invitation.scopeId}-${index}`}
                    style={{
                      marginBottom:
                        index < invitations.length - 1 ? spacingToPx(2) : 0,
                      paddingLeft: spacingToPx(isSmallScreen ? 1.5 : 2),
                      paddingRight: spacingToPx(isSmallScreen ? 1.5 : 2),
                      paddingBottom:
                        index === invitations.length - 1
                          ? spacingToPx(isSmallScreen ? 1.5 : 2)
                          : 0,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: spacingToPx(1),
                      }}
                    >
                      <div>
                        <Typography
                          style={{
                            marginRight: spacingToPx(1),
                            fontSize: isSmallScreen ? "0.875rem" : "1rem",
                          }}
                        >
                          {invitation.inviterName} invite you to join&nbsp;
                          {invitation.scopeName}
                        </Typography>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: spacingToPx(1),
                        }}
                      >
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() =>
                            handleAcceptInvitation(invitation.scopeId)
                          }
                          disabled={
                            loadingInvitationIds[invitation.scopeId] &&
                            isRunning === "ACCEPT"
                          }
                          style={{
                            textTransform: "none",
                            boxShadow: "none",
                          }}
                        >
                          Accept ✔{" "}
                          {loadingInvitationIds[invitation.scopeId] &&
                            isRunning === "ACCEPT" && (
                              <CircularProgress
                                size={16}
                                style={{ marginLeft: spacingToPx(1) }}
                              />
                            )}
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() =>
                            handleRejectInvitation(invitation.scopeId)
                          }
                          disabled={
                            loadingInvitationIds[invitation.scopeId] &&
                            isRunning === "REJECT"
                          }
                          style={{
                            textTransform: "none",
                            boxShadow: "none",
                          }}
                        >
                          Reject ✘{" "}
                          {loadingInvitationIds[invitation.scopeId] &&
                            isRunning === "REJECT" && (
                              <CircularProgress
                                size={16}
                                style={{ marginLeft: spacingToPx(1) }}
                              />
                            )}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              {invitations.length === 0 && (
                <div
                  style={{
                    textAlign: "center",
                    color: "hsl(var(--muted-foreground))",
                    paddingLeft: spacingToPx(isSmallScreen ? 1.5 : 2),
                    paddingRight: spacingToPx(isSmallScreen ? 1.5 : 2),
                    paddingBottom: spacingToPx(isSmallScreen ? 1.5 : 2),
                  }}
                >
                  <Typography variant="body1">No invitations</Typography>
                </div>
              )}
            </Card>

            {developerEmail && (
              <Card
                style={{
                  width: "100%",
                  backgroundColor: getComponentBgColor("hsl(var(--card))"),
                  fontFamily: "Roboto, sans-serif",
                  boxShadow: "none",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                    minHeight: "40px",
                    paddingLeft: spacingToPx(isSmallScreen ? 1.5 : 2),
                    paddingRight: spacingToPx(isSmallScreen ? 1.5 : 2),
                  }}
                >
                  <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                    Testing
                  </Typography>
                </div>
                <Divider
                  style={{
                    marginBottom: spacingToPx(2),
                    marginLeft: spacingToPx(2),
                    marginRight: spacingToPx(2),
                  }}
                />
                <div
                  style={{
                    paddingLeft: spacingToPx(isSmallScreen ? 1.5 : 2),
                    paddingRight: spacingToPx(isSmallScreen ? 1.5 : 2),
                    paddingBottom: spacingToPx(isSmallScreen ? 1.5 : 2),
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: spacingToPx(1),
                      gap: spacingToPx(isSmallScreen ? 1 : 2),
                      flexWrap: isSmallScreen ? "wrap" : "nowrap",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: spacingToPx(0.5),
                        flexShrink: 0,
                      }}
                    >
                      <Typography variant="body2" style={{ fontWeight: 500 }}>
                        Execution Environment
                      </Typography>
                      <Tooltip title="Select execution environment">
                        <IconButton
                          size="small"
                          style={{
                            padding: spacingToPx(0.5),
                            marginLeft: spacingToPx(-0.25),
                          }}
                        >
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                    </div>
                    <ToggleButtonGroup
                      value={selectedEnv}
                      exclusive
                      onChange={(event, newValue) => {
                        if (newValue !== null) {
                          handleExecutionEnvChange(newValue);
                        }
                      }}
                      size="small"
                      style={{
                        flexShrink: isSmallScreen ? 1 : 0,
                        minWidth: 0,
                      }}
                    >
                      <ToggleButton value="PROD">Prod</ToggleButton>
                      <ToggleButton value="STAGING">Staging</ToggleButton>
                      <ToggleButton value="LOCAL">Local</ToggleButton>
                    </ToggleButtonGroup>
                  </div>
                </div>
              </Card>
            )}

            {/* Fix Data Section */}
            <Card
              style={{
                width: "100%",
                backgroundColor: getComponentBgColor("hsl(var(--card))"),
                fontFamily: "Roboto, sans-serif",
                boxShadow: "none",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  minHeight: "40px",
                  paddingLeft: spacingToPx(isSmallScreen ? 1.5 : 2),
                  paddingRight: spacingToPx(isSmallScreen ? 1.5 : 2),
                }}
              >
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                  Fix Data
                </Typography>
              </div>
              <Divider
                style={{
                  marginBottom: spacingToPx(2),
                  marginLeft: spacingToPx(2),
                  marginRight: spacingToPx(2),
                }}
              />
              <div
                style={{
                  paddingLeft: spacingToPx(isSmallScreen ? 1.5 : 2),
                  paddingRight: spacingToPx(isSmallScreen ? 1.5 : 2),
                  paddingBottom: spacingToPx(isSmallScreen ? 1.5 : 2),
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: spacingToPx(1),
                  }}
                >
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    Clean broken data
                  </Typography>
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={() => setCleanDataDialogOpen(true)}
                    disabled={cleanDataRunning}
                    size="small"
                    style={{
                      textTransform: "none",
                      minWidth: "120px",
                      whiteSpace: "nowrap",
                      height: "32px",
                    }}
                  >
                    {cleanDataRunning ? "Running..." : "Clean Data"}
                  </Button>
                </div>
              </div>
            </Card>

            <Dialog
              open={openAddDialog}
              onClose={handleAddMemberClose}
              variant="pops"
            >
              <DialogTitle>Invite Members</DialogTitle>
              <DialogContent>
                {newMembers.map((member, index) => (
                  <div key={index} style={{ marginBottom: spacingToPx(3) }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: spacingToPx(1),
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        style={{ marginRight: spacingToPx(2) }}
                      >
                        Member {index + 1}
                      </Typography>
                      <IconButton
                        onClick={() => handleRemoveMember(index)}
                        color="error"
                      >
                        <RemoveCircleIcon />
                      </IconButton>
                    </div>
                    <TextField
                      label="Email"
                      variant="outlined"
                      fullWidth
                      required
                      value={member}
                      onChange={(e) =>
                        handleMemberChange(index, e.target.value)
                      }
                      error={!!emailErrors[index]}
                      helperText={emailErrors[index]}
                      style={{ marginBottom: spacingToPx(2) }}
                    />
                  </div>
                ))}
                <Button
                  onClick={() => setNewMembers([...newMembers, ""])}
                  fullWidth
                  variant="outlined"
                  style={{ width: "100%", textTransform: "none" }}
                >
                  Add More
                </Button>
              </DialogContent>
              <DialogActions
                style={{
                  justifyContent: "space-between",
                  paddingLeft: spacingToPx(2),
                  paddingRight: spacingToPx(2),
                  paddingBottom: spacingToPx(2),
                }}
              >
                <Button
                  onClick={handleAddMemberClose}
                  style={{ textTransform: "none" }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleAddMembers}
                  color="primary"
                  disabled={
                    newMembers.some((member) => !member) ||
                    emailErrors.some((error) => error) ||
                    isRunning === "ADD_MEMBER"
                  }
                  style={{ textTransform: "none" }}
                >
                  Invite{" "}
                  {isRunning === "ADD_SCOPE" && (
                    <CircularProgress
                      size={16}
                      style={{ marginLeft: spacingToPx(1) }}
                    />
                  )}
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog
              open={confirmDeleteOpen}
              onClose={() => {
                setConfirmDeleteOpen(false);
                setConfirmDeleteText("");
              }}
              variant="pops"
            >
              <DialogTitle
                className="flex items-center gap-2"
                style={{
                  color: "hsl(var(--destructive))",
                  fontWeight: 700,
                  fontSize: "1.25rem",
                }}
              >
                <DeleteIcon style={{ fontSize: "1.5rem" }} />
                Delete Space - Warning!
              </DialogTitle>
              <DialogContent>
                <div
                  style={{
                    marginBottom: spacingToPx(2),
                    padding: spacingToPx(2),
                    backgroundColor: "hsl(var(--destructive) / 0.5)",
                    color: "hsl(var(--destructive-foreground))",
                    borderRadius: spacingToPx(1),
                    border: "1px solid",
                    borderColor: "hsl(var(--destructive))",
                  }}
                >
                  <Typography variant="body2">
                    Deleting this space will permanently remove:
                  </Typography>
                  <ul
                    style={{
                      marginTop: spacingToPx(1),
                      marginBottom: 0,
                      paddingLeft: spacingToPx(2),
                    }}
                  >
                    <li>All automations in this space</li>
                    <li>All dashboard cards and widgets</li>
                    <li>All space data and settings</li>
                    <li>All member access</li>
                  </ul>
                </div>
                <TextField
                  fullWidth
                  label={`Type '${selectedScope?.name}' to proceed`}
                  value={confirmDeleteText}
                  onChange={(e) => setConfirmDeleteText(e.target.value)}
                  error={
                    confirmDeleteText !== "" &&
                    confirmDeleteText !== selectedScope?.name
                  }
                  style={
                    {
                      // Focus styles handled by TextField component
                    }
                  }
                />
              </DialogContent>
              <DialogActions
                style={{
                  paddingLeft: spacingToPx(2),
                  paddingBottom: spacingToPx(2),
                }}
              >
                <Button
                  onClick={() => {
                    setConfirmDeleteOpen(false);
                    setConfirmDeleteText("");
                  }}
                  variant="outlined"
                  color="primary"
                  style={{ textTransform: "none" }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDeleteScope}
                  color="error"
                  variant="contained"
                  disabled={confirmDeleteText !== selectedScope?.name}
                  className="hover:bg-error-dark"
                  style={{
                    textTransform: "none",
                    paddingLeft: spacingToPx(3),
                    paddingRight: spacingToPx(3),
                    fontWeight: 600,
                  }}
                  onMouseEnter={(e) => {
                    if (!e.currentTarget.disabled) {
                      e.currentTarget.style.backgroundColor =
                        "hsl(var(--destructive-foreground))";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!e.currentTarget.disabled) {
                      e.currentTarget.style.backgroundColor =
                        "hsl(var(--destructive))";
                    }
                  }}
                >
                  Delete Permanently
                  {isRunning === "DELE_SCOPE" && (
                    <CircularProgress
                      size={16}
                      style={{ marginLeft: spacingToPx(1) }}
                    />
                  )}
                </Button>
              </DialogActions>
            </Dialog>
          </div>
        </div>
      )}

      {/* Dialogs */}
      <Dialog open={openDialog} onClose={handleDialogClose} variant="pops">
        <DialogTitle style={{ paddingBottom: spacingToPx(2) }}>
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            Create New Space
          </Typography>
        </DialogTitle>
        <DialogContent>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: spacingToPx(3),
            }}
          >
            <TextField
              placeholder="Space Name"
              fullWidth
              variant="outlined"
              value={newScopeName}
              onChange={(e) => {
                setNewScopeName(e.target.value);
                checkDuplicateName(e.target.value);
              }}
              error={!!error && error.includes("name")}
              helperText={error && error.includes("name") ? error : ""}
            />

            <div>
              <Typography
                variant="body2"
                style={{ marginBottom: spacingToPx(1), fontWeight: 500 }}
              >
                Home Location
              </Typography>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setNewSpaceLocationDialogOpen(true)}
                startIcon={<LocationOnIcon />}
                style={{
                  paddingTop: spacingToPx(1.5),
                  paddingBottom: spacingToPx(1.5),
                  textTransform: "none",
                  justifyContent: "flex-start",
                  color: newSpaceLocation
                    ? "hsl(var(--foreground))"
                    : "hsl(var(--muted-foreground))",
                  borderColor: newSpaceLocation
                    ? "hsl(var(--primary))"
                    : "hsl(var(--muted-foreground))",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "hsl(var(--primary))";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = newSpaceLocation
                    ? "hsl(var(--primary))"
                    : "hsl(var(--muted-foreground))";
                }}
              >
                {newSpaceLocation
                  ? newSpaceLocation.address
                  : "Select Home Location"}
              </Button>
              {error && error.includes("location") && (
                <Typography
                  variant="caption"
                  style={{
                    color: "hsl(var(--destructive))",
                    marginTop: spacingToPx(0.5),
                    display: "block",
                  }}
                >
                  {error}
                </Typography>
              )}
            </div>
          </div>
        </DialogContent>
        <DialogActions
          style={{
            paddingLeft: spacingToPx(3),
            paddingRight: spacingToPx(3),
            paddingBottom: spacingToPx(3),
            paddingTop: spacingToPx(2),
          }}
        >
          <Button
            onClick={handleDialogClose}
            style={{
              textTransform: "none",
              paddingLeft: spacingToPx(3),
              paddingRight: spacingToPx(3),
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddScope}
            disabled={
              isRunning === "ADD_SCOPE" ||
              !newScopeName.trim() ||
              !newSpaceLocation
            }
            style={{
              textTransform: "none",
              paddingLeft: spacingToPx(3),
              paddingRight: spacingToPx(3),
              marginLeft: spacingToPx(1),
            }}
          >
            {isRunning === "ADD_SCOPE" ? (
              <>
                Creating...
                <CircularProgress
                  size={16}
                  style={{ marginLeft: spacingToPx(1) }}
                />
              </>
            ) : (
              "Create Space"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <LocationDialog
        open={locationDialogOpen}
        onClose={() => {
          setLocationDialogOpen(false);
          setEditLocation(null);
          setIsHomeLocationDialog(false);
        }}
        onSave={handleSaveLocation}
        onDelete={handleDeleteLocation}
        initialData={editLocation}
        locationNames={locationNames}
        isHomeLocation={isHomeLocationDialog}
      />

      <LocationDialog
        open={newSpaceLocationDialogOpen}
        onClose={() => setNewSpaceLocationDialogOpen(false)}
        onSave={handleNewSpaceLocationSave}
        isHomeLocation={true}
      />

      {/* Clean Data Dialog */}
      <Dialog
        open={cleanDataDialogOpen}
        onClose={() => setCleanDataDialogOpen(false)}
        variant="pops"
      >
        <DialogTitle>Clean Broken Data</DialogTitle>
        <DialogContent>
          <Typography variant="body1" style={{ marginBottom: spacingToPx(2) }}>
            This will clean up broken data in the current space, including:
          </Typography>
          <ul
            style={{
              paddingLeft: spacingToPx(2),
              marginBottom: spacingToPx(2),
            }}
          >
            <li>Cards with missing widgets</li>
            <li>Orphaned automations</li>
          </ul>
          <Typography
            variant="body2"
            style={{ color: "hsl(var(--muted-foreground))" }}
          >
            Use &quot;Dry Run&quot; to see what would be cleaned without making
            changes.
          </Typography>
        </DialogContent>
        <DialogActions
          style={{
            paddingLeft: spacingToPx(3),
            paddingRight: spacingToPx(3),
            paddingBottom: spacingToPx(3),
            paddingTop: spacingToPx(2),
            gap: spacingToPx(1),
          }}
        >
          <Button
            onClick={() => setCleanDataDialogOpen(false)}
            style={{ textTransform: "none" }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleCleanData(true)}
            disabled={cleanDataRunning}
            style={{ textTransform: "none" }}
            variant="outlined"
            color="info"
          >
            {cleanDataRunning ? (
              <>
                <CircularProgress
                  size={16}
                  style={{ marginRight: spacingToPx(1) }}
                />
                Running...
              </>
            ) : (
              "Dry Run"
            )}
          </Button>
          <Button
            onClick={() => handleCleanData(false)}
            disabled={cleanDataRunning}
            style={{ textTransform: "none" }}
            variant="contained"
            color="warning"
          >
            {cleanDataRunning ? (
              <>
                <CircularProgress
                  size={16}
                  style={{ marginRight: spacingToPx(1) }}
                />
                Cleaning...
              </>
            ) : (
              "Clean"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Exit Space Confirmation Dialog */}
      <Dialog
        open={confirmExitOpen}
        onClose={() => setConfirmExitOpen(false)}
        variant="pops"
      >
        <DialogTitle>Exit Space</DialogTitle>
        <DialogContent>
          <Typography variant="body1" style={{ marginBottom: spacingToPx(2) }}>
            Are you sure you want to exit this space?
          </Typography>
          <Typography variant="body2" color="textSecondary">
            You will lose access to this space&apos;s data and settings. You can
            rejoin if invited again by the space owner.
          </Typography>
        </DialogContent>
        <DialogActions
          style={{
            paddingLeft: spacingToPx(3),
            paddingBottom: spacingToPx(3),
            paddingTop: spacingToPx(2),
          }}
        >
          <Button
            onClick={() => setConfirmExitOpen(false)}
            style={{ textTransform: "none" }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmExitScope}
            style={{ textTransform: "none" }}
            variant="contained"
            color="warning"
          >
            Exit Space
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Scope;
